import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { controlLoading } from "@/services/slices/app.slice";
import { SignupAPI } from "@/services/api/user/signup";
import Modal from "@/components/Modal";
import { AUTH_CARD_CLASS } from "@/components/auth/authStyles";
import { universityList } from "@/assets/universityList";
import {
  buildSignupQuery,
  getSignupField,
  getSignupHKID,
  getSignupProfile,
  getSignupStudentCard,
  readSignupParams,
  setSignupField,
} from "./signupState";

const genderList = ["Male", "Female", "Others"];

const identityList = [
  "Undergraduate",
  "Postgraduate",
  "Teaching Staff",
  "Administrative or Research Staff",
  "Alumni",
  "Retiree",
];

const yearOfStudyList = [
  "Year one",
  "Year Two",
  "Year Three",
  "Year Four",
  "Year Five",
  "Year Six",
  "Year Seven",
  "Year Eight or above",
];

const selectClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffa217]";

/**
 * Signup step 6 — basic information. Ports the RN `BasicInformationScreen`:
 * university / gender / identity / year-of-study selection, then calls
 * `SignupAPI` to register the account. These selections have no place in the
 * query string, so they are mirrored into signup state on every change and
 * read back from it on mount — that is what survives a BACK to the HKID step.
 */
const BasicInformationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readSignupParams(search);

  const [university, setUniversity] = useState(() => getSignupField("university"));
  const [gender, setGender] = useState(() => getSignupField("gender"));
  const [identity, setIdentity] = useState(() => getSignupField("identity"));
  const [yearOfStudy, setYearOfStudy] = useState(() => getSignupField("yearOfStudy"));
  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");
  const [accepted, setAccepted] = useState(false);

  const showYearOfStudy =
    identity === "Undergraduate" || identity === "Postgraduate";

  const onChangeUniversity = (value: string) => {
    setUniversity(value);
    setSignupField("university", value);
  };

  const onChangeGender = (value: string) => {
    setGender(value);
    setSignupField("gender", value);
  };

  /** Identity drives whether the year of study applies, so it resets it. */
  const onChangeIdentity = (value: string) => {
    setIdentity(value);
    setSignupField("identity", value);
    setYearOfStudy("");
    setSignupField("yearOfStudy", "");
  };

  const onChangeYearOfStudy = (value: string) => {
    setYearOfStudy(value);
    setSignupField("yearOfStudy", value);
  };

  const onPressNext = async () => {
    if (!accepted) {
      setModalAlertText(t("Please accept the Student Terms & Conditions and Privacy Policy"));
      return setModalAlertVisible(true);
    }
    if (showYearOfStudy && yearOfStudy === "") {
      setModalAlertText(t("Please select year of study"));
      return setModalAlertVisible(true);
    }
    const finalYearOfStudy = showYearOfStudy ? yearOfStudy : "";

    dispatch(controlLoading({ showLoading: true }));
    const resp = await SignupAPI(
      params.username || "",
      params.email || "",
      params.phoneNum || "",
      params.password || "",
      getSignupProfile(),
      getSignupStudentCard(),
      getSignupHKID(),
      university,
      gender,
      identity,
      finalYearOfStudy,
      accepted,
      "2026-10-13",
      (i18n.language || "en").startsWith("zh") ? "zh_hant" : "en",
    );
    if (resp.status === -20000) {
      setModalAlertText(t("Email Existed"));
      return setModalAlertVisible(true);
    }
    if (resp.status === -20001) {
      setModalAlertText(t("Username Existed"));
      return setModalAlertVisible(true);
    }
    if (resp.status === -20002) {
      setModalAlertText(t("Can`t register account"));
      return setModalAlertVisible(true);
    }
    if (resp.status === 0) {
      navigate("/signup/success");
    }
  };

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">
        {t("Basic Information")}
      </h1>

      <div className="mt-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("University")}
          </label>
          <select
            value={university}
            onChange={(e) => onChangeUniversity(e.target.value)}
            className={selectClass}
          >
            <option value="">{t("Select your univesity")}</option>
            {universityList.map((uni) => (
              <option key={uni.value} value={uni.value}>
                {t(uni.value)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("Gender")}
          </label>
          <select
            value={gender}
            onChange={(e) => onChangeGender(e.target.value)}
            className={selectClass}
          >
            <option value="">{t("Select your Gender")}</option>
            {genderList.map((item) => (
              <option key={item} value={item}>
                {t(item)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("Identity")}
          </label>
          <select
            value={identity}
            onChange={(e) => onChangeIdentity(e.target.value)}
            className={selectClass}
          >
            <option value="">{t("Select your Identity")}</option>
            {identityList.map((item) => (
              <option key={item} value={item}>
                {t(item)}
              </option>
            ))}
          </select>
        </div>

        {showYearOfStudy && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("Year Of Study*")}
            </label>
            <select
              value={yearOfStudy}
              onChange={(e) => onChangeYearOfStudy(e.target.value)}
              className={selectClass}
            >
              <option value="">{t("Select Year Of Study")}</option>
              {yearOfStudyList.map((item) => (
                <option key={item} value={item}>
                  {t(item)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-start gap-2 mt-4">
          <input
            type="checkbox"
            id="terms"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-[#ffa217] focus:ring-[#ffa217]"
          />
          <label htmlFor="terms" className="text-sm text-gray-700">
            {t("I have read and agree to the Student Terms & Conditions and Privacy Policy")} (
            <Link to="/student-terms" target="_blank" rel="noopener noreferrer" className="text-[#ffa217] hover:underline">
              {t("Student Terms & Conditions")}
            </Link>{" "}
            &{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#ffa217] hover:underline">
              {t("Privacy Policy")}
            </Link>
            )
          </label>
        </div>

        <button
          type="button"
          onClick={onPressNext}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e]"
        >
          {t("NEXT")}
        </button>

        <button
          type="button"
          onClick={() => navigate(`/signup/hkid${buildSignupQuery({ ...params })}`)}
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
      </div>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BasicInformationPage;